import { request } from '../../api'
import { useServerFetch } from '../../composables/useServerFetch'

const SOFTWARE_RANKING_BASE_URL = 'http://api-wikitrader-go.fxeyeinterface.com'
const SOFTWARE_RANKING_EXIST_PATH = '/api/v1/softwate/check_software_ranking_exist'
const APP_DOWNLOAD_TAB_ID = '92200010'

export type RankCategorySubTab = {
  businessType: number
  name: string
  englishName?: string
}

export type RankCategoryItem = {
  code: string
  name: string
  flag?: string
  englishName?: string
  subTabs: RankCategorySubTab[]
}

export type RankCategoriesResponse = {
  items: RankCategoryItem[]
}

type RankCategoriesApiResponse = {
  data?: RankCategoriesResponse
  code?: number
  message?: string
  succeed?: boolean
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const getRankCategoriesResponse = () => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/rank/categories',
    method: 'GET' as const,
  }

  return import.meta.server
    ? useServerFetch<RankCategoriesApiResponse>(requestConfig)
    : request<RankCategoriesApiResponse>(requestConfig)
}

const getSoftwareRankingExist = () => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? SOFTWARE_RANKING_BASE_URL : api.v4,
    url: SOFTWARE_RANKING_EXIST_PATH,
    method: 'GET' as const,
  }

  return import.meta.server ? useServerFetch<unknown>(requestConfig) : request<unknown, unknown>(requestConfig)
}

const resolveRankCategoriesData = (response: unknown): RankCategoriesResponse => {
  let payload = response

  for (let depth = 0; depth < 4; depth += 1) {
    if (!isRecord(payload)) {
      break
    }

    if (Array.isArray(payload.items)) {
      return payload as unknown as RankCategoriesResponse
    }

    payload = payload.data
  }

  return { items: [] }
}

const hasSoftwareRanking = (response: unknown) => {
  let payload = response

  for (let depth = 0; depth < 4; depth += 1) {
    if (!isRecord(payload)) {
      return false
    }

    if (payload.hasRanking === true) {
      return true
    }

    payload = payload.data
  }

  return false
}

export const getRankCategories = async () => {
  const { t } = useAppI18n()
  const [rankCategoriesResult, softwareRankingResult] = await Promise.allSettled([
    getRankCategoriesResponse(),
    getSoftwareRankingExist(),
  ])

  if (rankCategoriesResult.status === 'rejected') {
    throw rankCategoriesResult.reason
  }

  const rankCategoriesData = resolveRankCategoriesData(rankCategoriesResult.value)
  const shouldAddAppRanking =
    softwareRankingResult.status === 'fulfilled' && hasSoftwareRanking(softwareRankingResult.value)

  const hasAppRanking = rankCategoriesData.items.some((item) => item.code === APP_DOWNLOAD_TAB_ID)

  return {
    code: 0,
    message: 'success',
    succeed: true,
    data: {
      ...rankCategoriesData,
      items:
        shouldAddAppRanking && !hasAppRanking
          ? [
              ...rankCategoriesData.items,
              {
                code: APP_DOWNLOAD_TAB_ID,
                name: t('90048'),
                englishName: 'appdownload',
                subTabs: [],
              },
            ]
          : rankCategoriesData.items,
    },
  }
}

export type RankListParams = {
  keyCode: string
  businessType: number | string
  page: number
  pageSize: number
}

export interface RankLabel {
  labelType: number
  content: string
  borderColor: string
  backgroundColor: string
  fontColor: string
  image: string
}

export interface RankingOrder {
  content: string
  fontColor: string
}

export interface TraderRankItem {
  ultimateType: number
  isProduct: number
  priority: string
  localShortName: string
  localFullName: string
  userId: string
  languageCode: string
  traderCode: string
  logo: string
  fullName: string
  shortName: string
  score: string
  status: number
  annotation: string
  traderId: string
  color: string
  isTop200: boolean
  ico: string
  icoSquare: string
  flag: string
  countryCode: string
  registerCountry: string
  slogan: string
  isAdvance: boolean
  epc: boolean
  ultimate: string
  isAdv: boolean
  isvr: boolean
  type: number
  rankingOrder: RankingOrder
  fixed: boolean
  company: string
  business?: string
  businessOperation?: string
  cryptoRegulation?: string
  cryptoReguLation?: string
  isHideRating: boolean
  isWhite: boolean
  pureLScore: string
  showName: string
  exposureDesc: string
  exposureCount: number
  isBadStatus: boolean
  hitsInfo: string
  sortedLabels: RankLabel[]
  specialtyLabels: any[]
  subScript: unknown
  ultraBgColor: string
  ultraFontColor: string
  labels: RankLabel[]
  ultimateUltraIco: string
  ultimateUltraVideo: string
  ultimateUltraVideoIco: string
  viewCount: string
  viewCountUnit: string
  alias: string
  scoreIcon: string
  assetReserve?: string
  assetReserveAmount?: string
  assetReserveMoney?: string
  reserveAmount?: string
  assetReserveValue?: string
}

export interface RankListResponse {
  traderRank: TraderRankItem[]
  desc: string
  total?: number | string
  totalCount?: number | string
  count?: number | string
}

export const getRankList = (params: RankListParams) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/brand/ranklist',
    method: 'GET',
    params,
  }

  return import.meta.server ? useServerFetch<RankListResponse>(requestConfig) : request<RankListResponse>(requestConfig)
}
